/**
 * CitationNetworkSkeleton
 *
 * A skeleton loading state for the CitationNetwork component
 */

import React from 'react'
import { cn } from '@/lib/utils'

interface CitationNetworkSkeletonProps {
  className?: string
}

export const CitationNetworkSkeleton: React.FC<CitationNetworkSkeletonProps> = ({ className }) => {
  return (
    <div
      className={cn(
        'border-border bg-card my-4 flex animate-pulse flex-col overflow-hidden rounded-lg border shadow-sm transition-all',
        className
      )}
    >
      {/* Header */}
      <div className="flex items-center border-b p-3">
        <div className="flex items-center gap-2">
          <div className="size-4 rounded bg-gray-200 dark:bg-gray-700"></div>
          <div className="h-4 w-32 rounded bg-gray-200 dark:bg-gray-700"></div>
        </div>
      </div>

      {/* Network visualization placeholder */}
      <div className="p-4">
        <div className="flex h-[300px] w-full items-center justify-center rounded-lg bg-gray-100 dark:bg-gray-800">
          <div className="flex flex-col items-center">
            <svg
              width="120"
              height="120"
              viewBox="0 0 120 120"
              xmlns="http://www.w3.org/2000/svg"
              className="text-gray-300 dark:text-gray-600"
            >
              {/* Central node */}
              <circle cx="60" cy="60" r="12" fill="currentColor" />

              {/* Connected nodes */}
              <circle cx="30" cy="40" r="8" fill="currentColor" />
              <circle cx="90" cy="40" r="8" fill="currentColor" />
              <circle cx="40" cy="90" r="8" fill="currentColor" />
              <circle cx="80" cy="90" r="8" fill="currentColor" />
              <circle cx="20" cy="70" r="6" fill="currentColor" />
              <circle cx="100" cy="70" r="6" fill="currentColor" />

              {/* Connection lines */}
              <line x1="60" y1="60" x2="30" y2="40" stroke="currentColor" strokeWidth="2" />
              <line x1="60" y1="60" x2="90" y2="40" stroke="currentColor" strokeWidth="2" />
              <line x1="60" y1="60" x2="40" y2="90" stroke="currentColor" strokeWidth="2" />
              <line x1="60" y1="60" x2="80" y2="90" stroke="currentColor" strokeWidth="2" />
              <line x1="30" y1="40" x2="20" y2="70" stroke="currentColor" strokeWidth="1" />
              <line x1="90" y1="40" x2="100" y2="70" stroke="currentColor" strokeWidth="1" />
            </svg>
            <div className="mt-4 h-4 w-32 rounded bg-gray-200 dark:bg-gray-700"></div>
          </div>
        </div>
      </div>

      {/* Citation details */}
      <div className="border-t p-4">
        <div className="space-y-4">
          {/* Citation item */}
          {[...Array(3)].map((_, i) => (
            <div key={i} className="flex gap-3">
              <div className="size-8 shrink-0 rounded-full bg-gray-200 dark:bg-gray-700"></div>
              <div className="flex-1 space-y-2">
                <div className="h-4 w-3/4 rounded bg-gray-200 dark:bg-gray-700"></div>
                <div className="h-3 w-full rounded bg-gray-200 dark:bg-gray-700"></div>
                <div className="h-3 w-5/6 rounded bg-gray-200 dark:bg-gray-700"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
